import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{
    id: string;
    title?: string;
    route?: string;
    score?: number;
  }>;
  timestamp: Date;
  isStreaming?: boolean;
}

interface HelpState {
  // Panel state
  isOpen: boolean;
  activeTab: 'docs' | 'chat';
  currentRoute: string;
  routeHistory: string[];

  // Chat state
  messages: ChatMessage[];
  isLoading: boolean;
  sessionId: string;

  // Documentation state
  docContent: string | null;
  docLoading: boolean;
  docError: string | null;

  // Actions
  open: (tab?: 'docs' | 'chat') => void;
  close: () => void;
  toggle: () => void;
  setActiveTab: (tab: 'docs' | 'chat') => void;
  setCurrentRoute: (route: string) => void;
  navigateToDoc: (route: string) => void;
  goBack: () => void;
  canGoBack: () => boolean;

  // Chat actions
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  appendToMessage: (id: string, content: string) => void;
  setLoading: (loading: boolean) => void;
  clearMessages: () => void;

  // Documentation actions
  setDocContent: (content: string | null) => void;
  setDocLoading: (loading: boolean) => void;
  setDocError: (error: string | null) => void;
}

const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const generateSessionId = () => `help_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const useHelpStore = create<HelpState>()(
  devtools(
    (set, get) => ({
      // Initial state
      isOpen: false,
      activeTab: 'docs',
      currentRoute: '/overview',
      routeHistory: [],
      messages: [],
      isLoading: false,
      sessionId: generateSessionId(),
      docContent: null,
      docLoading: false,
      docError: null,

      // Panel actions
      open: (tab) =>
        set({
          isOpen: true,
          activeTab: tab || get().activeTab,
        }),

      close: () => set({ isOpen: false }),

      toggle: () => set({ isOpen: !get().isOpen }),

      setActiveTab: (tab) => set({ activeTab: tab }),

      // Set route without adding to history (used for external route sync)
      setCurrentRoute: (route) => {
        const currentRoute = get().currentRoute;
        if (route !== currentRoute) {
          set({
            currentRoute: route,
            routeHistory: [], // Clear history when route changes externally
            docContent: null,
            docError: null,
          });
        }
      },

      // Navigate to doc with history tracking (used for internal doc links)
      navigateToDoc: (route) => {
        const currentRoute = get().currentRoute;
        if (route !== currentRoute) {
          set({
            routeHistory: [...get().routeHistory, currentRoute],
            currentRoute: route,
            docContent: null,
            docError: null,
          });
        }
      },

      // Go back to previous doc
      goBack: () => {
        const history = get().routeHistory;
        if (history.length > 0) {
          const previousRoute = history[history.length - 1];
          set({
            routeHistory: history.slice(0, -1),
            currentRoute: previousRoute,
            docContent: null,
            docError: null,
          });
        }
      },

      canGoBack: () => get().routeHistory.length > 0,

      // Chat actions
      addMessage: (message) => {
        const id = generateId();
        set({
          messages: [
            ...get().messages,
            {
              ...message,
              id,
              timestamp: new Date(),
            },
          ],
        });
        return id;
      },

      updateMessage: (id, updates) =>
        set({
          messages: get().messages.map((msg) =>
            msg.id === id ? { ...msg, ...updates } : msg
          ),
        }),

      appendToMessage: (id, content) =>
        set({
          messages: get().messages.map((msg) =>
            msg.id === id ? { ...msg, content: msg.content + content } : msg
          ),
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      clearMessages: () =>
        set({
          messages: [],
          sessionId: generateSessionId(),
        }),

      // Documentation actions
      setDocContent: (content) => set({ docContent: content }),
      setDocLoading: (loading) => set({ docLoading: loading }),
      setDocError: (error) => set({ docError: error }),
    }),
    { name: 'help-store' }
  )
);

// Selectors for optimized re-renders
export const useHelpOpen = () => useHelpStore((s) => s.isOpen);
export const useHelpActiveTab = () => useHelpStore((s) => s.activeTab);
export const useHelpCurrentRoute = () => useHelpStore((s) => s.currentRoute);
export const useHelpMessages = () => useHelpStore((s) => s.messages);
export const useHelpLoading = () => useHelpStore((s) => s.isLoading);
